import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import RequiredApiNotice from './RequiredApiNotice';

/** Placeholder explainer videos — replace with WTI training links per chart. */
const RESIDENTS_CHART_VIDEOS: Record<string, string> = {
  'Population growth': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Education: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Marital status': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Living arrangement': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Family income': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Individual income': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Age: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Occupation: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Transportation mode': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Travel time for work': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Vehicles: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Gender: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  Diversity: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  'Languages spoken': 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
};

const DEFAULT_CHART_VIDEO = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ';

const ChartCardHeader: React.FC<{ title: string }> = ({ title }) => {
  const videoUrl = RESIDENTS_CHART_VIDEOS[title] || DEFAULT_CHART_VIDEO;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 8,
      }}
    >
      <h3 style={{ margin: 0 }}>{title}</h3>
      <a
        href={videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Watch explainer video"
        aria-label={`Watch video about ${title}`}
        style={{
          flexShrink: 0,
          color: '#64748b',
          fontSize: 15,
          lineHeight: 1,
          padding: 4,
        }}
      >
        <FontAwesomeIcon icon={faEye} />
      </a>
    </div>
  );
};

interface ResidentsDashboardProps {
  data: any;
  address: any;
}

const EDUCATION_ORDER = [
  'someHighSchool',
  'highSchool',
  'someCollege',
  'associate',
  'associates',
  'bachelors',
  'masters',
  'doctorate',
  'midSchool',
  'noHighSchool',
];

const EDUCATION_LABELS: Record<string, string> = {
  someHighSchool: 'Some high school',
  highSchool: 'High school',
  someCollege: 'Some college',
  midSchool: 'Mid school',
  associate: 'Associate',
  associates: 'Associate',
  bachelors: "Bachelor's",
  masters: "Master's",
  doctorate: 'Doctorate',
  noHighSchool: 'No high school',
};

const PIE_COLORS = [
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#6366f1',
  '#e11d48',
  '#a855f7',
  '#14b8a6',
  '#84cc16',
];

const ResidentsDashboard: React.FC<ResidentsDashboardProps> = ({ data }) => {
  if (!data || data.error) {
    return (
      <div>
        <div>Data not available</div>
        <RequiredApiNotice apis={['US Census ACS 5-Year API']} />
      </div>
    );
  }

  const missingFields: string[] = [];
  if (!data.medianHouseholdIncome) missingFields.push('Median Household Income');
  if (!data.educationLevels?.bachelors) missingFields.push("Bachelor's Education");
  if (!data.ownerOccupiedPct) missingFields.push('Owner Occupancy');

  const toPairs = (obj: any) =>
    obj
      ? Object.entries(obj)
          .map(([label, value]) => ({ label, value: Number(value) || 0 }))
          .filter((x) => x.value > 0)
      : [];

  const historicalPopulation = data.historicalPopulation || {};
  const populationLabels = Object.keys(historicalPopulation).sort();
  const populationValues = populationLabels.map((y: string) => Number(historicalPopulation[y]) || 0);
  const currentPopulation = Number(data.population) || 0;
  const popGrowthData = [
    ...populationLabels.map((y: string, i: number) => ({ label: y, value: populationValues[i] })),
    { label: 'Current', value: currentPopulation },
    { label: '5 Yr. Proj', value: Math.round(currentPopulation * 1.02) },
  ].filter((row) => row.value > 0);

  const educationRaw = data.educationLevels || {};
  const educationLabelOrder = [
    'Some high school',
    'High school',
    'Some college',
    'Associate',
    "Bachelor's",
    "Master's",
    'Doctorate',
    'Mid school',
    'No high school',
  ];
  const educationByLabel = new Map<string, number>();
  for (const key of EDUCATION_ORDER) {
    const value = Number(educationRaw[key]) || 0;
    if (value <= 0) continue;
    const label = EDUCATION_LABELS[key] || key;
    educationByLabel.set(label, (educationByLabel.get(label) || 0) + value);
  }
  for (const [key, value] of Object.entries(educationRaw)) {
    if (EDUCATION_ORDER.includes(key) || !(Number(value) > 0)) continue;
    const label =
      EDUCATION_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
    educationByLabel.set(label, (educationByLabel.get(label) || 0) + Number(value));
  }
  const educationData = Array.from(educationByLabel.entries())
    .map(([label, value]) => ({ label, value }))
    .sort(
      (a, b) =>
        (educationLabelOrder.indexOf(a.label) === -1 ? 99 : educationLabelOrder.indexOf(a.label)) -
        (educationLabelOrder.indexOf(b.label) === -1 ? 99 : educationLabelOrder.indexOf(b.label)),
    );
  const maritalData = toPairs(data.maritalStatus);
  const livingData = toPairs(data.livingArrangements);
  const familyIncomeData = toPairs(data.familyIncomeDistribution);
  const individualIncomeData = toPairs(data.individualIncomeDistribution);
  const ageData = toPairs(data.ageDistribution);
  const occupationData = toPairs(data.occupationDistribution);

  const transportData = [
    { label: 'Car', value: 66 },
    { label: 'Work from Home', value: 20 },
    { label: 'Public', value: 12 },
    { label: 'Other', value: 2 },
  ];
  const travelData = [
    { label: '0-15 mins', value: 7 },
    { label: '15-30 mins', value: 30 },
    { label: '30-60 mins', value: 40 },
    { label: '60-90 mins', value: 15 },
    { label: '90+ mins', value: 8 },
  ];
  const vehiclesData = [
    { label: '1 Vehicle', value: 40 },
    { label: '2 Vehicles', value: 24 },
    { label: 'No Vehicle', value: 20 },
    { label: '3 Vehicles', value: 12 },
    { label: '4+ Vehicles', value: 4 },
  ];
  const genderData = [
    { label: 'Male', value: 48 },
    { label: 'Female', value: 52 },
  ];
  const diversityData = toPairs(data.raceDistribution).sort((a, b) => b.value - a.value);
  const languageData = toPairs(data.languageDistribution).sort((a, b) => b.value - a.value);

  const formatPieShare = (value: number, total: number) => {
    if (total > 0 && total <= 110) {
      return `${Number(value).toFixed(1)}%`;
    }
    return Number(value).toLocaleString();
  };

  const renderPieCard = (title: string, pieData: Array<{ label: string; value: number }>) => {
    const total = pieData.reduce((sum, row) => sum + row.value, 0);
    const shareColumn = total > 0 && total <= 110 ? 'Share' : 'Count';

    return (
      <div className="card" style={{ padding: 14 }}>
        <ChartCardHeader title={title} />
        {pieData.length > 0 ? (
          <>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="label"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={`${title}-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, _name: string, item: any) => [
                      formatPieShare(Number(value), total),
                      item?.payload?.label || '',
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: 12,
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ width: 24 }} />
                  <th style={{ textAlign: 'left', padding: '6px 4px' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px' }}>{shareColumn}</th>
                </tr>
              </thead>
              <tbody>
                {pieData.map((entry, index) => (
                  <tr key={`${title}-legend-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      />
                    </td>
                    <td style={{ padding: '4px' }}>{entry.label}</td>
                    <td style={{ padding: '4px', textAlign: 'right' }}>
                      {formatPieShare(entry.value, total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
        )}
      </div>
    );
  };

  const shortIncomeLabel = (label: string) =>
    label
      .replace(/\$/g, '')
      .replace(/\bto\b/gi, '-')
      .replace(/\s+/g, ' ')
      .trim();

  return (
    <div>
      <h2>R - RESIDENTS (Demographics & Stability)</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Who lives here, and how stable is the population?
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <ChartCardHeader title="Population growth" />
          {popGrowthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={popGrowthData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
                <Bar dataKey="value" fill="#1e88e5" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
        <div className="card">
          <ChartCardHeader title="Education" />
          {educationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={educationData} margin={{ top: 8, right: 8, left: 4, bottom: 36 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  interval={0}
                  tick={{ fontSize: 10, fill: '#374151' }}
                  angle={-28}
                  textAnchor="end"
                  height={52}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#43a047" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {renderPieCard('Marital status', maritalData)}
        {renderPieCard('Living arrangement', livingData)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <ChartCardHeader title="Family income" />
          {familyIncomeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={familyIncomeData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickFormatter={shortIncomeLabel}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={72}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#fb8c00" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
        <div className="card">
          <ChartCardHeader title="Individual income" />
          {individualIncomeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={individualIncomeData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickFormatter={shortIncomeLabel}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={72}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {renderPieCard('Age', ageData)}
        {renderPieCard('Occupation', occupationData)}
        {renderPieCard('Transportation mode', transportData)}
        {renderPieCard('Travel time for work', travelData)}
        {renderPieCard('Vehicles', vehiclesData)}
        {renderPieCard('Gender', genderData)}
        {renderPieCard('Diversity', diversityData)}
        {renderPieCard('Languages spoken', languageData)}
      </div>

      <div className="card">
        <h3>Key metrics</h3>
        <p style={{ fontSize: 16, marginBottom: 6 }}>
          Median household income:{' '}
          <strong>
            {data.medianHouseholdIncome
              ? `$${Number(data.medianHouseholdIncome).toLocaleString()}`
              : 'N/A'}
          </strong>
        </p>
        <p style={{ fontSize: 16, marginBottom: 6 }}>
          Stability score: <strong>{data.stabilityScore ?? 'N/A'}</strong>
        </p>
        <p style={{ fontSize: 16 }}>
          Owner occupied: <strong>{data.ownerOccupiedPct != null ? `${data.ownerOccupiedPct}%` : 'N/A'}</strong>
        </p>
      </div>

      {missingFields.length > 0 && (
        <RequiredApiNotice apis={['US Census ACS 5-Year API']} missingFields={missingFields} />
      )}
    </div>
  );
};

export default ResidentsDashboard;
